/**
 * Frequency pricing tiers: one scope of work, offered at more than one
 * service frequency, each at its own per-visit price.
 *
 * The four frequencies are deliberately the schedule's own
 * (`SERIES_FREQUENCIES`) rather than a new list: the tier a customer accepts
 * is the series their visits will be booked on, and two vocabularies for the
 * same choice would need translating exactly where a mistake costs money.
 *
 * A tier's price is the quote as built, less a discount belonging to the
 * frequency — recurring work is cheaper per visit than a one-off, because the
 * home never gets far from clean. The discount travels through the pricing
 * engine's ordinary tax treatment (see `frequencyTierDiscount` in
 * calculations/pricing.ts); nothing here does arithmetic.
 *
 * This module owns the company-level defaults: which frequencies a company
 * ordinarily offers and at what discount. They are nine columns on
 * `companies`, NOT NULL with the defaults below and bounded by CHECK, so a
 * stored value has a shape before anything here reads it. There is nothing to
 * sanitize on the way out and nothing to merge on the way in — a request that
 * adjusts the weekly discount writes the weekly column and leaves the other
 * three frequencies untouched by construction.
 */

import { SERIES_FREQUENCIES, type DiscountType, type SeriesFrequency } from './constants/index.ts';

/** A company's standing answer for one frequency. */
export interface FrequencyTierDefault {
  /** Whether new quotes offer this frequency at all. */
  offered: boolean;
  /**
   * The discount off the quote as built, in basis points (1,500 = 15% off).
   * Company defaults are percentages only: a fixed amount can be typed onto a
   * particular quote, but as a standing rule it would mean something different
   * on every quote it landed on.
   */
  discountBasisPoints: number;
}

export interface FrequencyTierSettings {
  /**
   * Whether a new residential per-visit quote starts with the offered tiers
   * already on it. Off by default — offering the same job at four prices is a
   * pricing strategy, and no company should discover it has adopted one
   * because a deploy happened. The estimator can still switch tiers on per
   * quote either way.
   */
  offerOnNewQuotes: boolean;
  tiers: Readonly<Record<SeriesFrequency, FrequencyTierDefault>>;
}

/**
 * The starting discounts are the trade's own custom, stated conservatively:
 * the more often a crew is in the home, the less each visit fights, so weekly
 * earns the deepest cut. One-time is the quote as built — a 0% tier, present
 * so the customer's choice includes "just this once" at the price they were
 * actually quoted.
 *
 * These are the column defaults in migration 0002, and the two have to agree:
 * a company that has never touched the feature holds them in the database
 * rather than getting them from here.
 */
export const DEFAULT_FREQUENCY_TIER_SETTINGS: FrequencyTierSettings = {
  offerOnNewQuotes: false,
  tiers: {
    one_time: { offered: true, discountBasisPoints: 0 },
    weekly: { offered: true, discountBasisPoints: 1_500 },
    biweekly: { offered: true, discountBasisPoints: 1_000 },
    monthly: { offered: true, discountBasisPoints: 500 },
  },
};

/** The company columns the tier defaults live in, as stored. */
export interface FrequencyTierColumns {
  frequency_tiers_offer_on_new_quotes: number;
  frequency_tier_one_time_offered: number;
  frequency_tier_one_time_discount_basis_points: number;
  frequency_tier_weekly_offered: number;
  frequency_tier_weekly_discount_basis_points: number;
  frequency_tier_biweekly_offered: number;
  frequency_tier_biweekly_discount_basis_points: number;
  frequency_tier_monthly_offered: number;
  frequency_tier_monthly_discount_basis_points: number;
}

/**
 * Each frequency's two settings, twice over: `column` as the row stores it,
 * and `key` as the company update path names it (`UPDATABLE_COLUMNS` in
 * database/queries/companies.ts). One table rather than one per direction, so
 * the reader and the writer cannot drift, and a fifth frequency cannot be
 * added to `SERIES_FREQUENCIES` and be quietly forgotten by either.
 */
const TIER_FIELDS = {
  one_time: {
    offered: { column: 'frequency_tier_one_time_offered', key: 'frequencyTierOneTimeOffered' },
    discount: {
      column: 'frequency_tier_one_time_discount_basis_points',
      key: 'frequencyTierOneTimeDiscountBasisPoints',
    },
  },
  weekly: {
    offered: { column: 'frequency_tier_weekly_offered', key: 'frequencyTierWeeklyOffered' },
    discount: { column: 'frequency_tier_weekly_discount_basis_points', key: 'frequencyTierWeeklyDiscountBasisPoints' },
  },
  biweekly: {
    offered: { column: 'frequency_tier_biweekly_offered', key: 'frequencyTierBiweeklyOffered' },
    discount: {
      column: 'frequency_tier_biweekly_discount_basis_points',
      key: 'frequencyTierBiweeklyDiscountBasisPoints',
    },
  },
  monthly: {
    offered: { column: 'frequency_tier_monthly_offered', key: 'frequencyTierMonthlyOffered' },
    discount: {
      column: 'frequency_tier_monthly_discount_basis_points',
      key: 'frequencyTierMonthlyDiscountBasisPoints',
    },
  },
} as const satisfies Readonly<
  Record<SeriesFrequency, Record<'offered' | 'discount', { column: keyof FrequencyTierColumns; key: string }>>
>;

/** The stored columns as the settings object every client is served. */
export function frequencyTierSettings(company: FrequencyTierColumns): FrequencyTierSettings {
  return {
    offerOnNewQuotes: company.frequency_tiers_offer_on_new_quotes === 1,
    tiers: Object.fromEntries(
      SERIES_FREQUENCIES.map((frequency) => [
        frequency,
        {
          offered: company[TIER_FIELDS[frequency].offered.column] === 1,
          discountBasisPoints: company[TIER_FIELDS[frequency].discount.column],
        },
      ]),
    ) as Record<SeriesFrequency, FrequencyTierDefault>,
  };
}

/**
 * The nested request payload flattened to the keys the company update path
 * knows. Only what the request actually carries: a payload naming just the
 * weekly discount produces one key, so the other three frequencies and the
 * `offerOnNewQuotes` switch are not in the UPDATE at all and keep what they
 * hold. That is what used to need a read, a merge and a sanitize.
 */
export function frequencyTierChanges(input: {
  offerOnNewQuotes?: boolean | undefined;
  tiers?: Partial<Record<SeriesFrequency, { offered?: boolean | undefined; discountBasisPoints?: number | undefined }>>;
}): Record<string, boolean | number> {
  const changes: Record<string, boolean | number> = {};
  if (input.offerOnNewQuotes !== undefined) changes.frequencyTiersOfferOnNewQuotes = input.offerOnNewQuotes;
  for (const frequency of SERIES_FREQUENCIES) {
    const tier = input.tiers?.[frequency];
    if (!tier) continue;
    if (tier.offered !== undefined) changes[TIER_FIELDS[frequency].offered.key] = tier.offered;
    if (tier.discountBasisPoints !== undefined) {
      changes[TIER_FIELDS[frequency].discount.key] = tier.discountBasisPoints;
    }
  }
  return changes;
}

/**
 * The tier rows a quote starts with when tiers are switched on: the offered
 * frequencies, in the canonical order, each carrying the company's discount.
 * A 0% tier carries no discount at all rather than a zero-value percentage —
 * `null` is how the engine and the customer page both say "the price as
 * built".
 */
export function seedFrequencyTiers(
  settings: FrequencyTierSettings,
): Array<{ frequency: SeriesFrequency; discountType: 'percentage' | null; discountValue: number }> {
  return SERIES_FREQUENCIES.filter((frequency) => settings.tiers[frequency].offered).map((frequency) => ({
    frequency,
    ...standingTierDiscount(settings, frequency),
  }));
}

/** A tier's discount, in the two fields a quote's tier row stores it as. */
export interface TierDiscount {
  discountType: 'percentage' | null;
  discountValue: number;
}

/**
 * The company's standing discount for one frequency, as a tier carries it.
 *
 * A frequency the company does not offer has no standing discount, whatever
 * its stored basis points say: the settings page disables that frequency's
 * percentage rather than zeroing it (see settings/pricing-card.tsx), so the
 * number left behind is one nobody can see or edit. A tier for such a
 * frequency starts at the price as built, and the estimator types what the
 * customer is being offered.
 */
export function standingTierDiscount(settings: FrequencyTierSettings, frequency: SeriesFrequency): TierDiscount {
  const tier = settings.tiers[frequency];
  const bp = tier.offered ? tier.discountBasisPoints : 0;
  return { discountType: bp > 0 ? 'percentage' : null, discountValue: bp > 0 ? bp : 0 };
}

/**
 * The tiers a quote should hold once another frequency is about to join them.
 *
 * A quote offering one frequency is quoting one price — the price as built,
 * with nothing to be cheaper than — which is why a quote generated for weekly
 * work carries a single weekly tier at no discount. The moment a second
 * frequency is offered that stops being true: the customer is now choosing
 * between frequencies, and a weekly option priced level with a one-off is not
 * an offer any company means to make. So the tier that was alone adopts the
 * company's standing discount for its frequency, exactly as it would have if
 * the quote had been seeded with the whole set to begin with.
 *
 * Only the sole tier is touched, and only while it carries no discount of its
 * own: once a quote offers a choice, every discount on it — typed, adjusted or
 * deliberately cleared — is the estimator's, and nothing here overwrites it.
 *
 * A lone tier is the one case the quote cannot answer for itself: emptied by
 * an estimator and never filled in by a generator look the same stored. That
 * distinction belongs to whoever watched it happen, so a caller holding a
 * cleared frequency is expected not to offer that tier here (`setTierOffered`
 * in the pricing step is the one that does).
 */
export function adoptStandingDiscountOnSoleTier<
  T extends { frequency: SeriesFrequency; discountType: DiscountType | null; discountValue: number },
>(tiers: readonly T[], settings: FrequencyTierSettings): T[] {
  const sole = tiers.length === 1 ? tiers[0] : undefined;
  if (!sole || sole.discountType !== null) return [...tiers];
  const standing = standingTierDiscount(settings, sole.frequency);
  if (standing.discountType === null) return [...tiers];
  return [{ ...sole, discountType: standing.discountType, discountValue: standing.discountValue }];
}
